import type { PrV2CommentResponse, PrV2ReviewResponse, PrV2ReviewThread } from '@/lib/pr-v2/types';

import type { PendingComments } from '../pending-comments-types';
import type {
  PrV2LocalCommentLifecycle,
  PrV2LocalScopeState,
  PrV2LocalThreadLifecycle,
} from './types';

import { mapPendingCommentToPrV2Comment } from '../comment-read-model';
import {
  COMMENT_ALIAS_PREFIX,
  findCommentClientId,
  findReviewClientId,
  findThreadClientId,
  getCommentAliasKeys,
  getCommentLookupIds,
  getReviewAliasKeys,
  getReviewLookupIds,
  getThreadAliasKeys,
  getThreadLookupIds,
  isNumericId,
  mergeLookupIds,
  unique,
} from './identity';
import { PR_V2_LOCAL_THREAD_LIFECYCLES } from './types';

import { getPrV2CommentLifecycle } from '@/lib/pr-v2/comment-lifecycle';

const LIFECYCLE_PRIORITY = {
  queued: 0,
  submitting: 1,
  persisted: 2,
} as const;

function lifecyclePriority(lifecycle: PrV2LocalCommentLifecycle): number {
  return LIFECYCLE_PRIORITY[lifecycle] ?? LIFECYCLE_PRIORITY.queued;
}

function getSourceLifecycle(
  comment: PrV2CommentResponse,
  overrideLifecycle?: PrV2LocalCommentLifecycle,
): PrV2LocalCommentLifecycle {
  return overrideLifecycle ?? getPrV2CommentLifecycle(comment);
}

function getThreadLifecycle(
  overrideLifecycle?: PrV2LocalThreadLifecycle,
): PrV2LocalThreadLifecycle {
  return overrideLifecycle ?? PR_V2_LOCAL_THREAD_LIFECYCLES.persisted;
}

export function upsertCommentEntity(
  scope: PrV2LocalScopeState,
  comment: PrV2CommentResponse,
  overrideLifecycle?: PrV2LocalCommentLifecycle,
): PrV2LocalScopeState {
  const lifecycle = getSourceLifecycle(comment, overrideLifecycle);
  const existingClientId = findCommentClientId(scope, comment);

  if (!existingClientId) {
    const clientId = `comment-client:${scope.nextCommentId}`;
    const lookupIds = getCommentLookupIds(comment);
    const nextScope: PrV2LocalScopeState = {
      ...scope,
      nextCommentId: scope.nextCommentId + 1,
      commentsByClientId: {
        ...scope.commentsByClientId,
        [clientId]: {
          clientId,
          comment,
          lifecycle,
          lookupIds,
        },
      },
      commentAliasToClientId: { ...scope.commentAliasToClientId },
      commentOrder: [...scope.commentOrder, clientId],
    };

    for (const aliasKey of getCommentAliasKeys(comment)) {
      nextScope.commentAliasToClientId[aliasKey] = clientId;
    }

    return nextScope;
  }

  const existingEntity = scope.commentsByClientId[existingClientId];
  if (!existingEntity) {
    return scope;
  }

  const nextLifecycle =
    lifecyclePriority(lifecycle) >= lifecyclePriority(existingEntity.lifecycle)
      ? lifecycle
      : existingEntity.lifecycle;
  // Keep the most advanced lifecycle snapshot on the entity. This prevents queued snapshots
  // from briefly overwriting a submitting/persisted entity during review-submit overlap.
  const nextComment =
    lifecyclePriority(lifecycle) >= lifecyclePriority(existingEntity.lifecycle)
      ? comment
      : existingEntity.comment;
  const nextLookupIds = mergeLookupIds(
    existingEntity.lookupIds,
    getCommentLookupIds(comment),
    existingEntity.comment,
    comment,
  );
  const nextAliasMap = { ...scope.commentAliasToClientId };

  for (const aliasKey of getCommentAliasKeys(comment)) {
    nextAliasMap[aliasKey] = existingClientId;
  }

  return {
    ...scope,
    commentsByClientId: {
      ...scope.commentsByClientId,
      [existingClientId]: {
        ...existingEntity,
        comment: nextComment,
        lifecycle: nextLifecycle,
        lookupIds: nextLookupIds,
      },
    },
    commentAliasToClientId: nextAliasMap,
  };
}

export function upsertReviewEntity(
  scope: PrV2LocalScopeState,
  review: PrV2ReviewResponse,
): PrV2LocalScopeState {
  const existingClientId = findReviewClientId(scope, review);

  if (!existingClientId) {
    const clientId = `review-client:${scope.nextReviewId}`;
    const nextScope: PrV2LocalScopeState = {
      ...scope,
      nextReviewId: scope.nextReviewId + 1,
      reviewsByClientId: {
        ...scope.reviewsByClientId,
        [clientId]: {
          clientId,
          review,
          lookupIds: getReviewLookupIds(review),
        },
      },
      reviewAliasToClientId: { ...scope.reviewAliasToClientId },
      reviewOrder: [...scope.reviewOrder, clientId],
    };

    for (const aliasKey of getReviewAliasKeys(review)) {
      nextScope.reviewAliasToClientId[aliasKey] = clientId;
    }

    return nextScope;
  }

  const existingEntity = scope.reviewsByClientId[existingClientId];
  if (!existingEntity) {
    return scope;
  }

  const nextAliasMap = { ...scope.reviewAliasToClientId };
  for (const aliasKey of getReviewAliasKeys(review)) {
    nextAliasMap[aliasKey] = existingClientId;
  }

  return {
    ...scope,
    reviewsByClientId: {
      ...scope.reviewsByClientId,
      [existingClientId]: {
        ...existingEntity,
        review,
        lookupIds: unique([...existingEntity.lookupIds, ...getReviewLookupIds(review)]),
      },
    },
    reviewAliasToClientId: nextAliasMap,
  };
}

export function upsertThreadEntity(
  scope: PrV2LocalScopeState,
  thread: PrV2ReviewThread,
  overrideLifecycle?: PrV2LocalThreadLifecycle,
): PrV2LocalScopeState {
  const lifecycle = getThreadLifecycle(overrideLifecycle);
  const existingClientId = findThreadClientId(scope, thread);

  if (!existingClientId) {
    const clientId = `thread-client:${scope.nextThreadId}`;
    const nextScope: PrV2LocalScopeState = {
      ...scope,
      nextThreadId: scope.nextThreadId + 1,
      threadsByClientId: {
        ...scope.threadsByClientId,
        [clientId]: {
          clientId,
          thread,
          lifecycle,
          optimisticResolvedOverride: null,
          lookupIds: getThreadLookupIds(thread),
        },
      },
      threadAliasToClientId: { ...scope.threadAliasToClientId },
      threadOrder: [...scope.threadOrder, clientId],
    };

    for (const aliasKey of getThreadAliasKeys(thread)) {
      nextScope.threadAliasToClientId[aliasKey] = clientId;
    }

    return nextScope;
  }

  const existingEntity = scope.threadsByClientId[existingClientId];
  if (!existingEntity) {
    return scope;
  }

  const nextAliasMap = { ...scope.threadAliasToClientId };
  for (const aliasKey of getThreadAliasKeys(thread)) {
    nextAliasMap[aliasKey] = existingClientId;
  }

  return {
    ...scope,
    threadsByClientId: {
      ...scope.threadsByClientId,
      [existingClientId]: {
        ...existingEntity,
        thread,
        lifecycle:
          lifecycle === PR_V2_LOCAL_THREAD_LIFECYCLES.persisted
            ? PR_V2_LOCAL_THREAD_LIFECYCLES.persisted
            : existingEntity.lifecycle,
        optimisticResolvedOverride:
          existingEntity.optimisticResolvedOverride === null ||
          thread.isResolved === existingEntity.optimisticResolvedOverride
            ? null
            : existingEntity.optimisticResolvedOverride,
        lookupIds: unique([...existingEntity.lookupIds, ...getThreadLookupIds(thread)]),
      },
    },
    threadAliasToClientId: nextAliasMap,
  };
}

export function setThreadResolvedOptimistic(
  scope: PrV2LocalScopeState,
  thread: PrV2ReviewThread,
  resolved: boolean,
): PrV2LocalScopeState {
  const nextScope = upsertThreadEntity(scope, thread);
  const clientId = findThreadClientId(nextScope, thread);
  if (!clientId) {
    return nextScope;
  }

  const existingEntity = nextScope.threadsByClientId[clientId];
  if (!existingEntity) {
    return nextScope;
  }

  return {
    ...nextScope,
    threadsByClientId: {
      ...nextScope.threadsByClientId,
      [clientId]: {
        ...existingEntity,
        optimisticResolvedOverride: resolved,
      },
    },
  };
}

export function clearThreadResolvedOptimistic(
  scope: PrV2LocalScopeState,
  lookupIds: string[],
): PrV2LocalScopeState {
  if (lookupIds.length === 0) {
    return scope;
  }

  const lookupIdSet = new Set(lookupIds);
  let nextThreadsByClientId: PrV2LocalScopeState['threadsByClientId'] | null = null;

  for (const clientId of scope.threadOrder) {
    const entity = scope.threadsByClientId[clientId];
    if (
      !entity ||
      entity.optimisticResolvedOverride === null ||
      !entity.lookupIds.some((lookupId) => lookupIdSet.has(lookupId))
    ) {
      continue;
    }

    nextThreadsByClientId ??= { ...scope.threadsByClientId };
    nextThreadsByClientId[clientId] = {
      ...entity,
      optimisticResolvedOverride: null,
    };
  }

  if (!nextThreadsByClientId) {
    return scope;
  }

  return {
    ...scope,
    threadsByClientId: nextThreadsByClientId,
  };
}

export function pruneMissingQueuedComments(
  scope: PrV2LocalScopeState,
  pendingComments: PendingComments[],
): PrV2LocalScopeState {
  const activePendingIds = new Set(
    pendingComments.map(
      (pendingComment) => mapPendingCommentToPrV2Comment(pendingComment).githubCommentId,
    ),
  );
  let nextScope = scope;

  for (const clientId of scope.commentOrder) {
    const entity = nextScope.commentsByClientId[clientId];
    if (!entity) {
      continue;
    }

    if (entity.lifecycle !== 'queued') {
      continue;
    }

    const hasActivePendingId = entity.lookupIds.some((lookupId) => activePendingIds.has(lookupId));
    if (hasActivePendingId) {
      continue;
    }

    const nextCommentsByClientId = { ...nextScope.commentsByClientId };
    const nextAliasMap = { ...nextScope.commentAliasToClientId };
    delete nextCommentsByClientId[clientId];

    for (const [aliasKey, mappedClientId] of Object.entries(nextAliasMap)) {
      if (mappedClientId === clientId) {
        delete nextAliasMap[aliasKey];
      }
    }

    nextScope = {
      ...nextScope,
      commentsByClientId: nextCommentsByClientId,
      commentAliasToClientId: nextAliasMap,
      commentOrder: nextScope.commentOrder.filter((id) => id !== clientId),
    };
  }

  return nextScope;
}

/**
 * Drop persisted entities that no longer exist in the latest canonical comments snapshot.
 *
 * Without this, the local overlay would keep showing deleted server comments after the query cache
 * has removed them, because the local model intentionally outlives individual optimistic writes.
 */
export function removeCommentClientIds(
  scope: PrV2LocalScopeState,
  clientIdsToRemove: Set<string>,
): PrV2LocalScopeState {
  if (clientIdsToRemove.size === 0) {
    return scope;
  }

  const nextCommentsByClientId = { ...scope.commentsByClientId };
  const nextAliasMap = { ...scope.commentAliasToClientId };

  for (const clientId of clientIdsToRemove) {
    delete nextCommentsByClientId[clientId];
  }

  for (const [aliasKey, mappedClientId] of Object.entries(nextAliasMap)) {
    if (clientIdsToRemove.has(mappedClientId)) {
      delete nextAliasMap[aliasKey];
    }
  }

  return {
    ...scope,
    commentsByClientId: nextCommentsByClientId,
    commentAliasToClientId: nextAliasMap,
    commentOrder: scope.commentOrder.filter((clientId) => !clientIdsToRemove.has(clientId)),
  };
}

export function pruneMissingPersistedComments(
  scope: PrV2LocalScopeState,
  comments: PrV2CommentResponse[],
): PrV2LocalScopeState {
  const activeClientIds = new Set<string>();
  for (const comment of comments) {
    const clientId = findCommentClientId(scope, comment);
    if (clientId) {
      activeClientIds.add(clientId);
    }
  }

  const clientIdsToRemove = new Set<string>();
  for (const clientId of scope.commentOrder) {
    const entity = scope.commentsByClientId[clientId];
    if (!entity || entity.lifecycle !== 'persisted') {
      continue;
    }

    if (!activeClientIds.has(clientId)) {
      clientIdsToRemove.add(clientId);
    }
  }

  return removeCommentClientIds(scope, clientIdsToRemove);
}

/**
 * Drop canonically removed threads while preserving threads that still carry an active optimistic
 * resolution overlay. That lets resolve/unresolve survive transient empty/stale refetches without
 * turning the local thread model into an append-only cache for truly deleted server threads.
 */
export function removeThreadClientIds(
  scope: PrV2LocalScopeState,
  clientIdsToRemove: Set<string>,
): PrV2LocalScopeState {
  if (clientIdsToRemove.size === 0) {
    return scope;
  }

  const nextThreadsByClientId = { ...scope.threadsByClientId };
  const nextAliasMap = { ...scope.threadAliasToClientId };

  for (const clientId of clientIdsToRemove) {
    delete nextThreadsByClientId[clientId];
  }

  for (const [aliasKey, mappedClientId] of Object.entries(nextAliasMap)) {
    if (clientIdsToRemove.has(mappedClientId)) {
      delete nextAliasMap[aliasKey];
    }
  }

  return {
    ...scope,
    threadsByClientId: nextThreadsByClientId,
    threadAliasToClientId: nextAliasMap,
    threadOrder: scope.threadOrder.filter((clientId) => !clientIdsToRemove.has(clientId)),
  };
}

export function pruneMissingPersistedThreads(
  scope: PrV2LocalScopeState,
  reviewThreads: PrV2ReviewThread[],
): PrV2LocalScopeState {
  const activeClientIds = new Set<string>();
  for (const reviewThread of reviewThreads) {
    const clientId = findThreadClientId(scope, reviewThread);
    if (clientId) {
      activeClientIds.add(clientId);
    }
  }

  const clientIdsToRemove = new Set<string>();
  for (const clientId of scope.threadOrder) {
    const entity = scope.threadsByClientId[clientId];
    if (
      !entity ||
      entity.lifecycle !== PR_V2_LOCAL_THREAD_LIFECYCLES.persisted ||
      entity.optimisticResolvedOverride !== null
    ) {
      continue;
    }

    if (!activeClientIds.has(clientId)) {
      clientIdsToRemove.add(clientId);
    }
  }

  return removeThreadClientIds(scope, clientIdsToRemove);
}

export function removeByLookupIds<T extends { lookupIds: string[] }>(
  items: Record<string, T>,
  aliasToClientId: Record<string, string>,
  order: string[],
  lookupIds: string[],
): {
  items: Record<string, T>;
  aliasToClientId: Record<string, string>;
  order: string[];
} {
  const lookupSet = new Set(lookupIds);
  const clientIdsToRemove = new Set<string>();

  for (const [clientId, entity] of Object.entries(items)) {
    if (entity.lookupIds.some((lookupId) => lookupSet.has(lookupId))) {
      clientIdsToRemove.add(clientId);
    }
  }

  if (clientIdsToRemove.size === 0) {
    return { items, aliasToClientId, order };
  }

  const nextItems = { ...items };
  const nextAliasMap = { ...aliasToClientId };

  for (const clientId of clientIdsToRemove) {
    delete nextItems[clientId];
  }

  for (const [aliasKey, clientId] of Object.entries(nextAliasMap)) {
    if (clientIdsToRemove.has(clientId)) {
      delete nextAliasMap[aliasKey];
    }
  }

  return {
    items: nextItems,
    aliasToClientId: nextAliasMap,
    order: order.filter((clientId) => !clientIdsToRemove.has(clientId)),
  };
}

export function linkCommentIdsInScope(
  scope: PrV2LocalScopeState,
  fromId: string,
  toId: string,
): PrV2LocalScopeState {
  const existingClientId =
    scope.commentAliasToClientId[`${COMMENT_ALIAS_PREFIX.lookup}${fromId}`] ??
    scope.commentAliasToClientId[`${COMMENT_ALIAS_PREFIX.server}${fromId}`] ??
    scope.commentAliasToClientId[`${COMMENT_ALIAS_PREFIX.view}${fromId}`];

  if (!existingClientId) {
    return scope;
  }

  const existingEntity = scope.commentsByClientId[existingClientId];
  if (!existingEntity) {
    return scope;
  }

  return {
    ...scope,
    commentsByClientId: {
      ...scope.commentsByClientId,
      [existingClientId]: {
        ...existingEntity,
        lookupIds: unique([...existingEntity.lookupIds, toId]),
      },
    },
    commentAliasToClientId: {
      ...scope.commentAliasToClientId,
      [`${COMMENT_ALIAS_PREFIX.lookup}${toId}`]: existingClientId,
      ...(isNumericId(toId) ? { [`${COMMENT_ALIAS_PREFIX.server}${toId}`]: existingClientId } : {}),
    },
  };
}
